/**
 * Dynamic Integration Flow Component
 * 
 * Progressive wizard that adapts to:
 * 1. The funnel's strategy (determines which steps are shown)
 * 2. User choices at each step (conditional logic)
 * 
 * Reuses existing OAuth implementations:
 * - Meta Ads OAuth
 * - Google Ads via Nango
 * - GHL Private App OAuth
 * - WordPress Divi/Elementor export
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
  ChevronRight, 
  ChevronLeft,
  CheckCircle2, 
  Circle,
  Loader2,
  ExternalLink,
  Zap,
  Globe,
  Mail,
  MessageCircle,
  Layout,
  Layers,
  Send,
  Activity,
  BookOpen,
  XCircle,
  Download,
  Facebook,
  Link2,
  Plug,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Icon mapping for info cards
const ICON_MAP = {
  facebook: Facebook,
  google: Globe,
  'x-circle': XCircle,
  layout: Layout,
  layers: Layers,
  zap: Zap,
  download: Download,
  mail: Mail,
  send: Send,
  activity: Activity,
  'book-open': BookOpen,
  'message-circle': MessageCircle,
};

/**
 * Integration Info Card Component
 */
const IntegrationCard = ({ 
  option, 
  info, 
  selected, 
  onSelect, 
  disabled = false,
  connected = false,
  loading = false 
}) => {
  const IconComponent = ICON_MAP[info?.icon] || Circle;
  const isComingSoon = info?.coming_soon;
  const isRecommended = info?.recommended;
  const hasPromo = info?.promo;
  
  return (
    <button
      onClick={() => !disabled && !isComingSoon && onSelect(option)}
      disabled={disabled || isComingSoon}
      className={`
        relative p-5 rounded-xl border-2 text-left transition-all w-full
        ${selected 
          ? 'border-brand bg-brand/5' 
          : isRecommended 
            ? 'border-green-500/50 bg-green-500/5 hover:border-green-500'
            : 'border-border-subtle hover:border-border-subtle/80 bg-surface'
        }
        ${disabled || isComingSoon ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
        ${connected ? 'ring-2 ring-green-500/30' : ''}
      `}
    >
      {/* Recommended badge */}
      {isRecommended && !connected && (
        <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
          ⭐ Recomendado
        </div>
      )}
      
      {/* Connected badge */}
      {connected && (
        <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-xs">
          <CheckCircle2 className="w-3 h-3" />
          Conectado
        </div>
      )}
      
      {/* Coming soon badge */}
      {isComingSoon && (
        <div className="absolute top-2 right-2 px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full text-xs">
          Próximamente
        </div>
      )}
      
      {/* Selection indicator */}
      {selected && !connected && !isRecommended && (
        <div className="absolute top-2 right-2">
          <CheckCircle2 className="w-5 h-5 text-brand" />
        </div>
      )}
      
      <div className="flex items-start gap-4">
        <div className={`
          w-12 h-12 rounded-xl flex items-center justify-center shrink-0
          ${selected ? 'bg-brand/20 text-brand' : isRecommended ? 'bg-green-500/20 text-green-500' : 'bg-app-bg text-text-muted'}
        `}>
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <IconComponent className="w-6 h-6" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-text-main">{info?.title || option}</h4>
          <p className="text-sm text-text-muted mt-1 line-clamp-2">
            {info?.description}
          </p>
          
          {/* Benefits list - only show if has benefits */}
          {info?.benefits && info.benefits.length > 0 && (
            <ul className="mt-3 space-y-1">
              {info.benefits.slice(0, 4).map((benefit, idx) => (
                <li key={idx} className="text-xs text-text-muted flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>
          )}
          
          {/* Promo for GHL */}
          {hasPromo && (
            <div className="mt-3 p-3 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/20">
              <p className="text-xs text-green-400 font-medium">{info.promo.text}</p>
              {info.promo.affiliate_url && (
                <a 
                  href={info.promo.affiliate_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-green-500 hover:text-green-400 font-semibold mt-2 px-3 py-1.5 bg-green-500/20 rounded-lg hover:bg-green-500/30 transition-all"
                  onClick={(e) => e.stopPropagation()}
                >
                  {info.promo.cta_text || 'Ver oferta'} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};


/**
 * WordPress Configuration Sub-step
 */
const WordPressConfig = ({ builder, onConnect, onCancel }) => {
  const { api } = useAuth();
  const [url, setUrl] = useState('');
  const [username, setUsername] = useState('admin');
  const [apiKey, setApiKey] = useState('');
  const [testing, setTesting] = useState(false);
  const [tested, setTested] = useState(false);
  
  const handleTest = async () => {
    if (!url) {
      toast.error('Ingresa la URL de tu WordPress');
      return;
    }
    if (!apiKey) {
      toast.error('La Application Password es obligatoria para publicar páginas');
      return;
    }
    
    setTesting(true);
    try {
      const res = await api.post('/integrations/wordpress/test', { 
        url, 
        api_key: apiKey,
        username: username || 'admin'
      });
      if (res.data?.success) {
        toast.success('Conexión exitosa con WordPress');
        setTested(true);
      } else {
        toast.error(res.data?.message || 'No se pudo conectar');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al probar conexión');
    } finally {
      setTesting(false);
    }
  };
  
  const handleConfirm = () => {
    onConnect({ url, apiKey, username, builder });
  };
  
  return (
    <div className="p-5 bg-surface rounded-xl border border-border-subtle">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
          <Globe className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h4 className="font-medium text-text-main">
            Configurar WordPress + {builder === 'divi' ? 'Divi' : 'Elementor'}
          </h4>
          <p className="text-xs text-text-muted mt-0.5">
            Las landings se crearán como páginas borrador en tu WordPress
          </p>
        </div>
      </div>
      
      {/* Requirements notice */}
      <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
        <p className="text-xs text-amber-300 font-medium mb-1">Requisitos:</p>
        <ul className="text-xs text-amber-300/80 space-y-0.5 list-disc list-inside">
          <li>WordPress 4.7 o superior con REST API habilitada</li>
          <li>{builder === 'divi' ? 'Divi Builder' : 'Elementor'} instalado y activo</li>
          <li>Application Password generada (ver instrucciones abajo)</li>
        </ul>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="text-sm text-text-muted block mb-1.5">URL de tu WordPress *</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://tusitio.com"
            className="w-full px-4 py-2.5 bg-app-bg border border-border-subtle rounded-lg text-text-main placeholder:text-text-muted focus:border-brand focus:outline-none"
          />
          <p className="text-xs text-text-muted mt-1">Sin barra final</p>
        </div>
        
        <div>
          <label className="text-sm text-text-muted block mb-1.5">Usuario de WordPress *</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="admin"
            className="w-full px-4 py-2.5 bg-app-bg border border-border-subtle rounded-lg text-text-main placeholder:text-text-muted focus:border-brand focus:outline-none"
          />
          <p className="text-xs text-text-muted mt-1">Usuario con permisos de administrador o editor</p>
        </div>
        
        <div>
          <label className="text-sm text-text-muted block mb-1.5">
            Application Password *
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
            className="w-full px-4 py-2.5 bg-app-bg border border-border-subtle rounded-lg text-text-main placeholder:text-text-muted focus:border-brand focus:outline-none"
          />
          <div className="mt-2 p-2.5 bg-surface-elevated rounded-lg border border-border-subtle">
            <p className="text-xs text-text-muted mb-1.5 font-medium">Cómo obtener la Application Password:</p>
            <ol className="text-xs text-text-muted space-y-1 list-decimal list-inside">
              <li>Entra a tu WordPress → Usuarios → Tu Perfil</li>
              <li>Baja hasta &quot;Application Passwords&quot;</li>
              <li>Escribe un nombre (ej: &quot;FascinAI&quot;) y haz clic en &quot;Añadir&quot;</li>
              <li>Copia la contraseña generada (solo se muestra una vez)</li>
            </ol>
          </div>
        </div>
        
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Cancelar
          </Button>
          
          {!tested ? (
            <Button
              onClick={handleTest}
              disabled={testing || !url || !apiKey}
              className="flex-1"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Probar conexión
            </Button>
          ) : (
            <Button
              onClick={handleConfirm}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Confirmar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};


/**
 * Main Dynamic Integration Flow Component
 */
const DynamicIntegrationFlow = ({ funnel, onComplete, onFunnelUpdate }) => {
  const { api, user } = useAuth();
  
  // DEBUG: Log component mount
  console.log('[DynamicIntegrationFlow] Mounted with:', { 
    funnelId: funnel?.id, 
    strategy: funnel?.strategy 
  });
  
  // State
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState([]);
  const [infoCards, setInfoCards] = useState({});
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  
  // Integration choices (saved to funnel)
  const [choices, setChoices] = useState({
    ads: { meta: false, google: false, none: false },
    web: null,
    emails: null,
    whatsapp: false
  });
  
  // Connection status
  const [connections, setConnections] = useState({
    meta_connected: false,
    google_connected: false,
    ghl_connected: false,
    wordpress_connected: false,
    email_connected: false
  });
  
  // Sub-step states
  const [showWordPressConfig, setShowWordPressConfig] = useState(null); // 'divi' or 'elementor'
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  
  // Meta Ad Account selector states
  const [showMetaAdAccountSelector, setShowMetaAdAccountSelector] = useState(false);
  const [metaAdAccounts, setMetaAdAccounts] = useState([]);
  const [selectedMetaAdAccount, setSelectedMetaAdAccount] = useState('');
  const [loadingAdAccounts, setLoadingAdAccounts] = useState(false);
  
  // Google Ads Account selector states
  const [showGoogleAdsAccountSelector, setShowGoogleAdsAccountSelector] = useState(false);
  const [googleAdsAccounts, setGoogleAdsAccounts] = useState([]);
  const [selectedGoogleAdsAccount, setSelectedGoogleAdsAccount] = useState('');
  const [loadingGoogleAdsAccounts, setLoadingGoogleAdsAccounts] = useState(false);
  
  // Google Ads Manual ID input states
  const [showGoogleAdsManualInput, setShowGoogleAdsManualInput] = useState(false);
  const [manualGoogleAdsId, setManualGoogleAdsId] = useState('');
  const [manualGoogleAdsManagerId, setManualGoogleAdsManagerId] = useState('');
  const [savingManualGoogleAdsId, setSavingManualGoogleAdsId] = useState(false);
  const [verifyingGoogleAdsId, setVerifyingGoogleAdsId] = useState(false);
  const [googleAdsVerificationResult, setGoogleAdsVerificationResult] = useState(null);
  
  // Manual Meta token states
  const [showManualMetaToken, setShowManualMetaToken] = useState(false);
  const [manualMetaToken, setManualMetaToken] = useState('');
  const [savingManualToken, setSavingManualToken] = useState(false);
  
  // Email Marketing Integration States
  const [showEmailConfig, setShowEmailConfig] = useState(null); // 'mailchimp', 'activecampaign', 'brevo'
  const [emailApiKey, setEmailApiKey] = useState('');
  const [emailApiUrl, setEmailApiUrl] = useState(''); // For ActiveCampaign
  const [emailConnecting, setEmailConnecting] = useState(false);
  const [emailConnected, setEmailConnected] = useState({});
  const [emailLists, setEmailLists] = useState([]);
  const [selectedEmailList, setSelectedEmailList] = useState('');
  const [loadingEmailLists, setLoadingEmailLists] = useState(false);
  const [exportingEmails, setExportingEmails] = useState(false);
  const [emailExportResult, setEmailExportResult] = useState(null);
  
  // Load config based on strategy
  useEffect(() => {
    const loadConfig = async () => {
      // If no strategy defined, use default config or show message
      if (!funnel?.strategy) {
        console.warn('[IntegrationFlow] No strategy defined for funnel');
        // Use default "all enabled" config
        setSteps([
          { step: 1, id: "ads", title: "Publicidad", description: "Conecta tus plataformas de publicidad", options: ["meta_ads", "google_ads", "ads_none"], multiple: true },
          { step: 2, id: "web", title: "Constructor de Landings", description: "¿Dónde publicarás tus páginas?", options: ["wordpress", "ghl", "web_none"], multiple: false },
          { step: 3, id: "emails", title: "Email Marketing", description: "Conecta tu herramienta de emails", options: ["ghl_email_promo", "mailchimp", "activecampaign", "brevo", "kajabi", "emails_none"], multiple: false }
        ]);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const res = await api.get(`/integrations/integration-config/${funnel.strategy}`);
        if (res.data?.success) {
          setSteps(res.data.steps || []);
          setInfoCards(res.data.info_cards || {});
        }
        
        // Load existing choices from funnel - merge with defaults to avoid undefined properties
        if (funnel.integration_choices) {
          setChoices(prevChoices => ({
            ...prevChoices,
            ...funnel.integration_choices,
            // Ensure ads object is always properly structured
            ads: {
              meta: false,
              google: false,
              none: false,
              ...(funnel.integration_choices.ads || {})
            }
          }));
        }
        
        // Check existing connections
        await checkConnections();
        
      } catch (err) {
        console.error('Failed to load integration config:', err);
        toast.error('Error al cargar configuración');
      } finally {
        setLoading(false);
      }
    };
    
    loadConfig();
  }, [funnel?.strategy, funnel?.id]); // eslint-disable-line
  
  // Check for OAuth callback via sessionStorage (set by FunnelWizard)
  useEffect(() => {
    const callbackData = sessionStorage.getItem('oauth_callback');
    if (callbackData) {
      try {
        const data = JSON.parse(callbackData);
        // Only process if callback is recent (within 30 seconds) and for this funnel
        const isRecent = Date.now() - data.timestamp < 30000;
        const isThisFunnel = data.funnel_id === funnel?.id;
        
        if (isRecent && isThisFunnel) {
          if (data.platform === 'meta') {
            setShowMetaAdAccountSelector(true);
            fetchMetaAdAccounts();
            toast.success(`Meta conectado${data.meta_user ? ` como ${data.meta_user}` : ''}`, {
              description: 'Selecciona tu cuenta publicitaria para continuar'
            });
          } else if (data.platform === 'google_ads') {
            setShowGoogleAdsAccountSelector(true);
            fetchGoogleAdsAccounts();
            toast.success('Google Ads conectado correctamente', {
              description: 'Selecciona tu cuenta publicitaria para continuar'
            });
          }
        }
        
        // Clear the callback data after processing
        sessionStorage.removeItem('oauth_callback');
      } catch (e) {
        console.error('Error parsing OAuth callback data:', e);
        sessionStorage.removeItem('oauth_callback');
      }
    }
    
    // Also check URL params as fallback (in case component mounts with params still present)
    const params = new URLSearchParams(window.location.search);
    
    // Meta OAuth callback
    if (params.get('meta_connected') === 'true' && params.get('select_ad_account') === 'true') {
      setShowMetaAdAccountSelector(true);
      fetchMetaAdAccounts();
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    
    // Google Ads OAuth callback
    if (params.get('google_ads_connected') === 'true' && params.get('select_ad_account') === 'true') {
      setShowGoogleAdsAccountSelector(true);
      fetchGoogleAdsAccounts();
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [funnel?.id]); // Re-run when funnel changes
  
  // Fetch Meta Ad Accounts
  const fetchMetaAdAccounts = async () => {
    setLoadingAdAccounts(true);
    try {
      const res = await api.get('/integrations/meta/ad-accounts', {
        params: { funnel_id: funnel?.id }
      });
      if (res.data) {
        setMetaAdAccounts(res.data);
      }
    } catch (err) {
      console.error('Error fetching Meta ad accounts:', err);
      toast.error('Error al cargar cuentas publicitarias');
    } finally {
      setLoadingAdAccounts(false);
    }
  };
  
  // Save selected Meta Ad Account
  const handleSaveMetaAdAccount = async () => {
    if (!selectedMetaAdAccount) {
      toast.error('Selecciona una cuenta publicitaria');
      return;
    }
    
    // Find the selected account to get its name
    const selectedAccount = metaAdAccounts.find(acc => acc.id === selectedMetaAdAccount);
    
    try {
      await api.post(`/funnels/${funnel.id}/integrations/meta/select-account`, {
        ad_account_id: selectedMetaAdAccount,
        ad_account_name: selectedAccount?.name || selectedAccount?.account_id || selectedMetaAdAccount
      });
      
      // Update local connection state
      setConnections(prev => ({ ...prev, meta_connected: true }));
      setShowMetaAdAccountSelector(false);
      toast.success('Cuenta publicitaria guardada');
      
      // Refresh connections
      await checkConnections();
    } catch (err) {
      toast.error('Error al guardar cuenta');
    }
  };
  
  // Fetch Google Ads Accounts
  const fetchGoogleAdsAccounts = async () => {
    setLoadingGoogleAdsAccounts(true);
    try {
      const res = await api.get('/integrations/google-ads/customers', {
        params: { funnel_id: funnel?.id }
      });
      if (res.data?.customers) {
        setGoogleAdsAccounts(res.data.customers);
      }
    } catch (err) {
      console.error('Error fetching Google Ads accounts:', err);
      toast.error('Error al cargar cuentas de Google Ads');
    } finally {
      setLoadingGoogleAdsAccounts(false);
    }
  };
  
  // Save selected Google Ads Account
  const handleSaveGoogleAdsAccount = async () => {
    if (!selectedGoogleAdsAccount) {
      toast.error('Selecciona una cuenta publicitaria');
      return;
    }
    
    // Find the selected account to get its manager_id
    const selectedAccount = googleAdsAccounts.find(acc => acc.customer_id === selectedGoogleAdsAccount);
    
    try {
      await api.post(`/integrations/google-ads/select-account?funnel_id=${funnel.id}`, {
        customer_id: selectedGoogleAdsAccount,
        manager_id: selectedAccount?.manager_id || null  // Pass manager_id for multi-tenant support
      });
      
      // Update local connection state
      setConnections(prev => ({ ...prev, google_connected: true }));
      setShowGoogleAdsAccountSelector(false);
      toast.success('Cuenta de Google Ads guardada');
      
      // Refresh connections
      await checkConnections();
    } catch (err) {
      toast.error('Error al guardar cuenta de Google Ads');
    }
  };
  
  // Save manually entered Google Ads Account ID
  const handleSaveManualGoogleAdsId = async () => {
    if (!manualGoogleAdsId.trim()) {
      toast.error('Introduce el ID de la cuenta de Google Ads');
      return;
    }
    
    // Clean the ID: remove dashes and spaces (user might enter 123-456-7890)
    const cleanedId = manualGoogleAdsId.replace(/[-\s]/g, '').trim();
    
    // Validate format: should be 10 digits
    if (!/^\d{10}$/.test(cleanedId)) {
      toast.error('El ID debe tener 10 dígitos (formato: 123-456-7890 o 1234567890)');
      return;
    }
    
    setSavingManualGoogleAdsId(true);
    try {
      const cleanedManagerId = manualGoogleAdsManagerId ? manualGoogleAdsManagerId.replace(/[-\s]/g, '').trim() : null;
      
      await api.post(`/integrations/google-ads/select-account?funnel_id=${funnel.id}`, {
        customer_id: cleanedId,
        manager_id: cleanedManagerId || null,
        manual_entry: true  // Flag to skip API validation
      });
      
      // Update local connection state
      setConnections(prev => ({ ...prev, google_connected: true }));
      setShowGoogleAdsAccountSelector(false);
      setShowGoogleAdsManualInput(false);
      setManualGoogleAdsId('');
      setManualGoogleAdsManagerId('');
      setGoogleAdsVerificationResult(null);
      toast.success('Cuenta de Google Ads guardada manualmente');
      
      // Refresh connections
      await checkConnections();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Error al guardar cuenta de Google Ads';
      toast.error(errorMsg);
    } finally {
      setSavingManualGoogleAdsId(false);
    }
  };
  
  // Verify manually entered Google Ads Account ID before saving
  const handleVerifyGoogleAdsId = async () => {
    if (!manualGoogleAdsId.trim()) {
      toast.error('Introduce el ID de la cuenta de Google Ads');
      return;
    }
    
    // Clean the ID
    const cleanedId = manualGoogleAdsId.replace(/[-\s]/g, '').trim();
    
    // Validate format
    if (!/^\d{10}$/.test(cleanedId)) {
      toast.error('El ID debe tener 10 dígitos (formato: 123-456-7890 o 1234567890)');
      return;
    }
    
    setVerifyingGoogleAdsId(true);
    setGoogleAdsVerificationResult(null);
    
    try {
      const cleanedManagerId = manualGoogleAdsManagerId ? manualGoogleAdsManagerId.replace(/[-\s]/g, '').trim() : null;
      
      const res = await api.post('/integrations/google-ads/verify-account', {
        customer_id: cleanedId,
        manager_id: cleanedManagerId || null
      });
      
      if (res.data?.valid) {
        setGoogleAdsVerificationResult({
          success: true,
          accountName: res.data.account_name,
          message: res.data.message
        });
        toast.success(`✓ ${res.data.message}`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'No se pudo verificar la cuenta';
      setGoogleAdsVerificationResult({
        success: false,
        message: errorMsg
      });
      toast.error(errorMsg);
    } finally {
      setVerifyingGoogleAdsId(false);
    }
  };
  
  // Check existing platform connections - ALWAYS from backend, never from stale funnel object
  const checkConnections = async () => {
    if (!funnel?.id) return;
    
    try {
      // Parallel requests to all canonical status endpoints
      const [metaRes, googleRes, ghlRes, wpRes] = await Promise.all([
        api.get('/integrations/meta/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} })),
        api.get('/integrations/google-ads/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} })),
        api.get('/integrations/crm/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} })),
        api.get('/integrations/wordpress/status', { params: { funnel_id: funnel.id } }).catch(() => ({ data: {} }))
      ]);
      
      // Read ONLY from backend responses, not from stale funnel object
      setConnections({
        meta_connected: metaRes.data?.connected || false,
        google_connected: googleRes.data?.connected || false,
        ghl_connected: ghlRes.data?.connected || false,
        wordpress_connected: wpRes.data?.connected || false,
        email_connected: ghlRes.data?.connected || false // If GHL is connected, email is too
      });
      
      console.log('[Connections] Status refreshed:', {
        meta: metaRes.data?.connected,
        google: googleRes.data?.connected,
        ghl: ghlRes.data?.connected,
        wordpress: wpRes.data?.connected
      });
      
    } catch (err) {
      console.error('Failed to check connections:', err);
    }
  };
  
  // Refresh funnel data from backend after OAuth connection
  const refreshFunnelData = async () => {
    if (!funnel?.id) return;
    try {
      const res = await api.get(`/funnels/${funnel.id}`);
      if (res.data) {
        // Update the funnel reference if callback is provided
        if (onFunnelUpdate && typeof onFunnelUpdate === 'function') {
          onFunnelUpdate(res.data);
        }
        // Log for debugging
        console.log('[OAuth] Funnel data refreshed');
      }
    } catch (err) {
      console.error('Failed to refresh funnel:', err);
    }
  };
  
  // Handle Manual Meta Token submission
  const handleSaveManualMetaToken = async () => {
    if (!manualMetaToken.trim()) {
      toast.error('Por favor ingresa el token de acceso');
      return;
    }
    
    if (!funnel?.id) {
      toast.error('Error: No se pudo identificar el funnel');
      return;
    }
    
    setSavingManualToken(true);
    try {
      const response = await api.post('/integrations/meta/connect-manual', {
        access_token: manualMetaToken.trim(),
        funnel_id: funnel.id
      });
      
      if (response.data?.success) {
        toast.success(response.data.message || 'Meta conectado correctamente');
        setConnections(prev => ({ ...prev, meta_connected: true }));
        setShowManualMetaToken(false);
        setManualMetaToken('');
        
        // Refresh funnel data to get updated integrations
        await checkConnections();
      }
    } catch (err) {
      console.error('Error saving manual token:', err);
      const errorMsg = err.response?.data?.detail || 'Error al conectar con Meta';
      toast.error(errorMsg);
    } finally {
      setSavingManualToken(false);
    }
  };
  
  // ==================== EMAIL MARKETING FUNCTIONS ====================
  
  // Connect email platform (Mailchimp, ActiveCampaign, Brevo)
  const handleConnectEmail = async (platform) => {
    if (!emailApiKey.trim()) {
      toast.error('Por favor ingresa la API Key');
      return;
    }
    
    if (platform === 'activecampaign' && !emailApiUrl.trim()) {
      toast.error('Por favor ingresa la URL de tu cuenta');
      return;
    }
    
    setEmailConnecting(true);
    try {
      let endpoint = '';
      let payload = {};
      
      if (platform === 'mailchimp') {
        endpoint = '/mailchimp/setup';
        payload = { api_key: emailApiKey.trim(), funnel_id: funnel?.id };
      } else if (platform === 'activecampaign') {
        endpoint = '/integrations/activecampaign/setup';
        payload = { api_key: emailApiKey.trim(), api_url: emailApiUrl.trim(), funnel_id: funnel?.id };
      } else if (platform === 'brevo') {
        endpoint = '/integrations/brevo/setup';
        payload = { api_key: emailApiKey.trim(), funnel_id: funnel?.id };
      }
      
      const res = await api.post(endpoint, payload);
      
      if (res.data?.success || res.data?.server_prefix || res.data?.account_name) {
        toast.success(`${platform === 'mailchimp' ? 'Mailchimp' : platform === 'activecampaign' ? 'ActiveCampaign' : 'Brevo'} conectado correctamente`);
        setEmailConnected(prev => ({ ...prev, [platform]: true }));
        setEmailApiKey('');
        setEmailApiUrl('');
        
        // Fetch lists
        await fetchEmailLists(platform);
      }
    } catch (err) {
      console.error(`Error connecting ${platform}:`, err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || `Error al conectar ${platform}`;
      toast.error(errorMsg);
    } finally {
      setEmailConnecting(false);
    }
  };
  
  // Fetch email lists/audiences
  const fetchEmailLists = async (platform) => {
    setLoadingEmailLists(true);
    try {
      let endpoint = '';
      if (platform === 'mailchimp') {
        endpoint = '/mailchimp/audiences';
      } else if (platform === 'activecampaign') {
        endpoint = '/integrations/activecampaign/lists';
      } else if (platform === 'brevo') {
        endpoint = '/integrations/brevo/lists';
      }
      
      const res = await api.get(endpoint);
      const lists = res.data?.lists || res.data?.audiences || res.data || [];
      setEmailLists(Array.isArray(lists) ? lists : []);
    } catch (err) {
      console.error(`Error fetching ${platform} lists:`, err);
      setEmailLists([]);
    } finally {
      setLoadingEmailLists(false);
    }
  };
  
  // Export email sequence
  const handleExportEmailSequence = async (platform) => {
    if (!selectedEmailList) {
      toast.error('Selecciona una lista primero');
      return;
    }
    
    setExportingEmails(true);
    setEmailExportResult(null);
    try {
      let endpoint = '';
      let payload = { funnel_id: funnel?.id };
      
      if (platform === 'mailchimp') {
        endpoint = '/mailchimp/sequences/export';
        payload.list_id = selectedEmailList;
      } else if (platform === 'activecampaign') {
        endpoint = '/integrations/activecampaign/export-sequence';
        payload.list_id = selectedEmailList;
      } else if (platform === 'brevo') {
        endpoint = '/integrations/brevo/export-sequence';
        payload.list_id = selectedEmailList;
      }
      
      const res = await api.post(endpoint, payload);
      
      if (res.data?.success || res.data?.exported_count !== undefined) {
        const count = res.data?.exported_count || res.data?.campaigns_created || res.data?.emails_exported || 0;
        setEmailExportResult({ success: true, count });
        toast.success(`¡${count} emails exportados a ${platform === 'mailchimp' ? 'Mailchimp' : platform === 'activecampaign' ? 'ActiveCampaign' : 'Brevo'}!`);
      }
    } catch (err) {
      console.error(`Error exporting to ${platform}:`, err);
      const errorMsg = err.response?.data?.detail || `Error al exportar a ${platform}`;
      toast.error(errorMsg);
      setEmailExportResult({ success: false, error: errorMsg });
    } finally {
      setExportingEmails(false);
    }
  };
  
  // Current step
  const currentStep = steps[currentStepIndex];
  
  // Handle option selection
  const handleSelect = (stepId, option) => {
    if (stepId === 'ads') {
      // Ads allows multiple selection (meta + google)
      if (option === 'ads_none') {
        setChoices(prev => ({ ...prev, ads: { meta: false, google: false, none: true } }));
      } else if (option === 'meta_ads') {
        setChoices(prev => ({ 
          ...prev, 
          ads: { ...prev.ads, meta: !prev.ads.meta, none: false } 
        }));
      } else if (option === 'google_ads') {
        setChoices(prev => ({ 
          ...prev, 
          ads: { ...prev.ads, google: !prev.ads.google, none: false } 
        }));
      }
    } else if (stepId === 'web') {
      if (option === 'wordpress') {
        setShowWordPressConfig('wordpress');
      } else {
        setChoices(prev => ({ ...prev, web: option }));
        setShowWordPressConfig(null);
      }
    } else if (stepId === 'emails') {
      // Handle email platform selection
      if (option === 'ghl_email_promo') {
        // Open affiliate link in new tab
        window.open('https://iautomationexperts.com/', '_blank');
        setChoices(prev => ({ ...prev, emails: option }));
      } else if (option === 'mailchimp' || option === 'activecampaign' || option === 'brevo') {
        // Show config panel for these platforms
        setShowEmailConfig(option);
        setChoices(prev => ({ ...prev, emails: option }));
        setEmailApiKey('');
        setEmailApiUrl('');
        setEmailLists([]);
        setSelectedEmailList('');
        setEmailExportResult(null);
      } else if (option === 'kajabi') {
        // Kajabi is "coming soon" - just select it
        setChoices(prev => ({ ...prev, emails: option }));
        toast.info('Kajabi estará disponible próximamente');
      } else {
        setChoices(prev => ({ ...prev, emails: option }));
        setShowEmailConfig(null);
      }
    }
  };
  
  // Handle OAuth connections
  // ==================== POPUP OAUTH HANDLER ====================
  // Opens OAuth in a popup window, keeps wizard open
  const openOAuthPopup = (url, platform) => {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    const popup = window.open(
      url,
      'oauth_popup',  // Use same name to reuse window
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
    
    if (!popup || popup.closed) {
      toast.error('Permite las ventanas emergentes para conectar');
      setConnectingPlatform(null);
      return null;
    }
    
    // Bring popup to front
    popup.focus();
    
    // Safety timeout - reset state after 90s if popup doesn't respond
    const safetyTimeout = setTimeout(() => {
      console.log('[OAuth] Safety timeout reached (90s)');
      window.removeEventListener('message', handleMessage);
      setConnectingPlatform(null);
      if (!popup.closed) {
        toast.info('La conexión tardó demasiado. Intenta de nuevo.');
      }
    }, 90000);
    
    // Listen for messages from popup
    const handleMessage = async (event) => {
      // Accept messages from our domains
      const validOrigins = ['fascinai.pro', 'emergentagent.com', 'localhost'];
      const isValidOrigin = validOrigins.some(domain => event.origin.includes(domain));
      if (!isValidOrigin) return;
      
      // Handle unified oauth_callback format (meta, google_ads, ghl)
      // Also handle legacy GHL_OAUTH_SUCCESS for backwards compatibility
      const isOAuthCallback = event.data?.type === 'oauth_callback' || 
                              event.data?.type === 'GHL_OAUTH_SUCCESS';
      
      if (isOAuthCallback) {
        console.log('[OAuth] Received postMessage:', event.data);
        clearTimeout(safetyTimeout);
        window.removeEventListener('message', handleMessage);
        
        // Normalize the data format
        const success = event.data.success ?? event.data.type === 'GHL_OAUTH_SUCCESS';
        const provider = event.data.provider || 
                        (event.data.type === 'GHL_OAUTH_SUCCESS' ? 'ghl' : 'unknown');
        const error = event.data.error;
        const meta_user = event.data.meta_user;
        
        if (success) {
          console.log(`[OAuth] Success for provider: ${provider}`);
          
          // ALWAYS refresh connection status AND funnel data after ANY OAuth success
          await Promise.all([
            checkConnections(),
            refreshFunnelData()
          ]);
          
          // Handle provider-specific UI (account selectors)
          if (provider === 'meta') {
            console.log('[OAuth] Opening Meta ad account selector');
            setShowMetaAdAccountSelector(true);
            fetchMetaAdAccounts();
            toast.success(`Meta conectado${meta_user ? ` como ${meta_user}` : ''}`, {
              description: 'Selecciona tu cuenta publicitaria'
            });
          } else if (provider === 'google_ads') {
            console.log('[OAuth] Opening Google Ads account selector');
            setShowGoogleAdsAccountSelector(true);
            fetchGoogleAdsAccounts();
            toast.success('Google Ads conectado correctamente', {
              description: 'Selecciona tu cuenta publicitaria'
            });
          } else if (provider === 'ghl') {
            toast.success('GoHighLevel conectado correctamente');
          } else {
            toast.success(`${provider} conectado correctamente`);
          }
        } else {
          console.log(`[OAuth] Error for provider: ${provider}`, error);
          toast.error(`Error al conectar: ${error || 'Desconocido'}`);
        }
        
        setConnectingPlatform(null);
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Check if popup was closed without completing
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        clearTimeout(safetyTimeout);
        window.removeEventListener('message', handleMessage);
        setConnectingPlatform(null);
      }
    }, 1000);
    
    return popup;
  };
  
  // ==================== DISCONNECT HANDLER ====================
  const [disconnecting, setDisconnecting] = useState(null);
  
  const handleDisconnect = async (provider) => {
    if (!confirm(`¿Estás seguro de que quieres desconectar ${provider}?`)) {
      return;
    }
    
    setDisconnecting(provider);
    try {
      let endpoint = '';
      let method = 'delete';
      
      switch (provider) {
        case 'meta':
        case 'meta_ads':
          endpoint = `/integrations/meta/disconnect?funnel_id=${funnel?.id}`;
          break;
        case 'google_ads':
          endpoint = `/integrations/google-ads/disconnect?funnel_id=${funnel?.id}`;
          break;
        case 'ghl':
          endpoint = `/integrations/crm/disconnect?funnel_id=${funnel?.id}`;
          break;
        case 'wordpress':
          endpoint = `/integrations/wordpress/disconnect?funnel_id=${funnel?.id}`;
          break;
        case 'mailchimp':
          endpoint = `/mailchimp/disconnect?funnel_id=${funnel?.id}`;
          break;
        case 'activecampaign':
          endpoint = `/integrations/activecampaign/disconnect?funnel_id=${funnel?.id}`;
          break;
        case 'brevo':
          endpoint = `/integrations/brevo/disconnect?funnel_id=${funnel?.id}`;
          break;
        default:
          toast.error(`Proveedor desconocido: ${provider}`);
          return;
      }
      
      await api.delete(endpoint);
      toast.success(`${provider} desconectado correctamente`);
      
      // Refresh connection status
      await checkConnections();
      
      // Clear any local state for that provider
      if (provider === 'meta' || provider === 'meta_ads') {
        setShowMetaAdAccountSelector(false);
        setMetaAdAccounts([]);
        setSelectedMetaAdAccount('');
      } else if (provider === 'google_ads') {
        setShowGoogleAdsAccountSelector(false);
        setGoogleAdsAccounts([]);
        setSelectedGoogleAdsAccount('');
      } else if (provider === 'mailchimp' || provider === 'activecampaign' || provider === 'brevo') {
        setShowEmailConfig(null);
        setEmailConnected(prev => ({ ...prev, [provider]: false }));
        setEmailLists([]);
        setSelectedEmailList('');
      }
      
    } catch (err) {
      console.error(`Error disconnecting ${provider}:`, err);
      toast.error(err.response?.data?.detail || `Error al desconectar ${provider}`);
    } finally {
      setDisconnecting(null);
    }
  };

  const handleConnect = async (platform) => {
    setConnectingPlatform(platform);
    
    try {
      const currentEnv = window.location.hostname.includes('preview') ? 'preview' : 'prod';
      
      if (platform === 'meta_ads') {
        // Meta OAuth - USE POPUP WINDOW (like Google Ads)
        if (!funnel?.id) {
          toast.error('Error: No se pudo identificar el funnel actual');
          setConnectingPlatform(null);
          return;
        }
        
        const res = await api.get('/integrations/meta/login-url', {
          params: { env: currentEnv, funnel_id: funnel.id }
        });
        if (res.data.url) {
          openOAuthPopup(res.data.url, 'meta');
        } else {
          toast.error('No se pudo obtener URL de autorización de Meta');
          setConnectingPlatform(null);
        }
      } else if (platform === 'google_ads') {
        // Google Ads OAuth - USE POPUP WINDOW
        if (!funnel?.id) {
          toast.error('Error: No se pudo identificar el funnel actual');
          setConnectingPlatform(null);
          return;
        }
        
        const res = await api.get('/integrations/google-ads/login-url', {
          params: { env: currentEnv, user_id: user?.id, funnel_id: funnel.id }
        });
        if (res.data.url) {
          openOAuthPopup(res.data.url, 'google_ads');
        } else {
          toast.error('No se pudo obtener URL de autorización');
          setConnectingPlatform(null);
        }
      } else if (platform === 'ghl') {
        // GHL Direct OAuth - USE POPUP WINDOW
        if (!funnel?.id) {
          toast.error('Error: No se pudo identificar el funnel actual');
          setConnectingPlatform(null);
          return;
        }
        
        try {
          // Use /crm/login-url endpoint (integrations.py)
          const res = await api.get('/integrations/crm/login-url', {
            params: { funnel_id: funnel.id, env: currentEnv }
          });
          
          if (res.data?.url) {
            openOAuthPopup(res.data.url, 'ghl');
          } else {
            toast.error('No se pudo obtener la URL de autorización de GHL');
            setConnectingPlatform(null);
          }
        } catch (err) {
          const errorMsg = err.response?.data?.detail || 'Error al conectar GoHighLevel';
          toast.error(errorMsg);
          setConnectingPlatform(null);
          return;
        }
      }
    } catch (err) {
      toast.error('Error al conectar');
      setConnectingPlatform(null);
    }
  };
  
  // Handle WordPress config
  const handleWordPressConnect = async (config) => {
    try {
      await api.post('/integrations/wordpress/config', {
        url: config.url,
        username: config.username,  // Include WordPress username
        api_key: config.apiKey,
        builder: config.builder,
        funnel_id: funnel.id
      });
      
      setChoices(prev => ({ ...prev, web: `wordpress_${config.builder}` }));
      setShowWordPressConfig(null);
      setConnections(prev => ({ ...prev, wordpress_connected: true }));
      toast.success('WordPress configurado correctamente');
    } catch (err) {
      toast.error('Error al configurar WordPress');
    }
  };
  
  // Save choices to funnel
  const saveChoices = async () => {
    try {
      await api.put(`/funnels/${funnel.id}`, {
        integration_choices: choices
      });
    } catch (err) {
      console.error('Failed to save choices:', err);
    }
  };
  
  // Navigation
  const canContinue = () => {
    if (!currentStep) return false;
    
    const stepId = currentStep.id;
    
    if (stepId === 'ads') {
      return choices?.ads?.meta || choices?.ads?.google || choices?.ads?.none || false;
    }
    if (stepId === 'web') {
      return choices?.web !== null;
    }
    if (stepId === 'emails') {
      // Skip if GHL is connected
      if (choices?.web === 'ghl' && connections?.ghl_connected) return true;
      return choices?.emails !== null;
    }
    if (stepId === 'whatsapp') {
      return true; // Always can continue (coming soon)
    }
    
    return true;
  };
  
  const handleNext = async () => {
    await saveChoices();
    
    if (currentStepIndex < steps.length - 1) {
      // Check if we should skip email step
      const nextStep = steps[currentStepIndex + 1];
      if (nextStep?.id === 'emails' && choices.web === 'ghl' && connections.ghl_connected) {
        // GHL handles emails, mark as connected
        setChoices(prev => ({ ...prev, emails: 'ghl_emails' }));
        
        // Skip to next step after emails
        if (currentStepIndex + 2 < steps.length) {
          setCurrentStepIndex(currentStepIndex + 2);
        } else {
          onComplete && onComplete(choices);
        }
        return;
      }
      
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      // Completed all steps
      await saveChoices();
      onComplete && onComplete(choices);
    }
  };
  
  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };
  
  // Render loading
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-brand mb-4" />
        <p className="text-text-muted text-sm">Cargando integraciones...</p>
      </div>
    );
  }
  
  // Render error state if no steps loaded
  if (!steps || steps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-yellow-500" />
        </div>
        <h3 className="text-lg font-semibold text-text-main mb-2">Configuración no disponible</h3>
        <p className="text-text-muted text-sm max-w-md">
          No se pudo cargar la configuración de integraciones. 
          Esto puede ocurrir si el funnel no tiene una estrategia definida.
        </p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={() => window.location.reload()}
        >
          Recargar página
        </Button>
      </div>
    );
  }
  
  // Render step content
  const renderStepContent = () => {
    if (!currentStep) return null;
    
    const stepId = currentStep.id;
    const options = currentStep.options || [];
    
    // Check if this is email step and GHL is connected
    if (stepId === 'emails' && choices.web === 'ghl' && connections.ghl_connected) {
      return (
        <div className="p-6 bg-green-500/10 border border-green-500/30 rounded-xl text-center">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-text-main">Emails vía GoHighLevel</h3>
          <p className="text-sm text-text-muted mt-2">
            Ya tienes GHL conectado. Los emails se enviarán automáticamente a través de tu cuenta de GoHighLevel.
          </p>
        </div>
      );
    }
    
    // WordPress sub-config
    if (showWordPressConfig) {
      return (
        <WordPressConfig
          builder={showWordPressConfig}
          onConnect={handleWordPressConnect}
          onCancel={() => setShowWordPressConfig(null)}
        />
      );
    }
    
    // Email Marketing Config Panel (Mailchimp, ActiveCampaign, Brevo)
    if (showEmailConfig && stepId === 'emails') {
      const platformName = showEmailConfig === 'mailchimp' ? 'Mailchimp' : 
                           showEmailConfig === 'activecampaign' ? 'ActiveCampaign' : 'Brevo';
      const isConnected = emailConnected[showEmailConfig];
      
      return (
        <div className="p-6 bg-surface border border-border-subtle rounded-xl space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl bg-brand/20">
                {showEmailConfig === 'mailchimp' ? '📧' : showEmailConfig === 'activecampaign' ? '⚡' : '📨'}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-main">Configurar {platformName}</h3>
                <p className="text-xs text-text-muted">Conecta tu cuenta para exportar la secuencia de emails</p>
              </div>
            </div>
            <button 
              onClick={() => setShowEmailConfig(null)}
              className="text-text-muted hover:text-text-main"
            >
              ✕
            </button>
          </div>
          
          {/* Connection Form */}
          {!isConnected ? (
            <div className="space-y-4 p-4 rounded-xl bg-app-bg border border-border-subtle">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-text-secondary">
                  <p className="font-medium text-blue-400 mb-1">¿Dónde encuentro mi API Key?</p>
                  {showEmailConfig === 'mailchimp' && (
                    <p className="text-xs">Ve a Mailchimp → Account → Extras → API keys → Create a key</p>
                  )}
                  {showEmailConfig === 'activecampaign' && (
                    <p className="text-xs">Ve a Settings → Developer → API Access (copia URL y Key)</p>
                  )}
                  {showEmailConfig === 'brevo' && (
                    <p className="text-xs">Ve a Brevo → SMTP & API → API Keys → Generate a new API key</p>
                  )}
                </div>
              </div>
              
              {showEmailConfig === 'activecampaign' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-main">URL de tu cuenta</label>
                  <input
                    type="text"
                    value={emailApiUrl}
                    onChange={(e) => setEmailApiUrl(e.target.value)}
                    placeholder="https://tuempresa.api-us1.com"
                    className="w-full px-3 py-2 bg-surface border border-border-subtle rounded-lg text-sm text-text-main placeholder-text-muted"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-main">API Key</label>
                <input
                  type="password"
                  value={emailApiKey}
                  onChange={(e) => setEmailApiKey(e.target.value)}
                  placeholder="Pega tu API Key aquí..."
                  className="w-full px-3 py-2 bg-surface border border-border-subtle rounded-lg text-sm text-text-main placeholder-text-muted font-mono"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowEmailConfig(null)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => handleConnectEmail(showEmailConfig)}
                  disabled={emailConnecting || !emailApiKey.trim() || (showEmailConfig === 'activecampaign' && !emailApiUrl.trim())}
                  className="flex-1"
                >
                  {emailConnecting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Conectando...</>
                  ) : (
                    <><Plug className="w-4 h-4 mr-2" />Conectar</>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            /* Connected - Show list selector and export button */
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-green-400 font-medium">✓ {platformName} conectado</span>
                </div>
                <button
                  onClick={() => handleDisconnect(showEmailConfig)}
                  disabled={disconnecting === showEmailConfig}
                  className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                >
                  {disconnecting === showEmailConfig ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <XCircle className="w-3 h-3" />
                  )}
                  Desconectar
                </button>
              </div>
              
              {/* List Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-main">Selecciona la lista de destino</label>
                {loadingEmailLists ? (
                  <div className="flex items-center gap-2 text-text-muted text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Cargando listas...
                  </div>
                ) : emailLists.length === 0 ? (
                  <div className="text-sm text-text-muted">
                    No se encontraron listas.{' '}
                    <button onClick={() => fetchEmailLists(showEmailConfig)} className="text-brand hover:underline">
                      Reintentar
                    </button>
                  </div>
                ) : (
                  <select
                    value={selectedEmailList}
                    onChange={(e) => setSelectedEmailList(e.target.value)}
                    className="w-full px-3 py-2 bg-surface border border-border-subtle rounded-lg text-sm text-text-main"
                  >
                    <option value="">Selecciona una lista...</option>
                    {emailLists.map(list => (
                      <option key={list.id || list.list_id} value={list.id || list.list_id}>
                        {list.name || list.title} ({list.stats?.member_count || list.subscriber_count || 0} contactos)
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              {/* Export Button */}
              <Button
                onClick={() => handleExportEmailSequence(showEmailConfig)}
                disabled={exportingEmails || !selectedEmailList}
                className="w-full bg-brand hover:bg-brand/90"
              >
                {exportingEmails ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exportando...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Exportar secuencia de emails</>
                )}
              </Button>
              
              {/* Export Result */}
              {emailExportResult && (
                <div className={`p-3 rounded-lg ${emailExportResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  {emailExportResult.success ? (
                    <p className="text-sm text-green-400">✓ {emailExportResult.count} emails exportados correctamente</p>
                  ) : (
                    <p className="text-sm text-red-400">✗ {emailExportResult.error}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    // Normal options grid
    return (
      <div className="grid gap-4">
        {options.map(option => {
          const info = infoCards[option];
          
          // Determine if selected - use optional chaining for safety
          let isSelected = false;
          if (stepId === 'ads') {
            if (option === 'meta_ads') isSelected = choices?.ads?.meta || false;
            else if (option === 'google_ads') isSelected = choices?.ads?.google || false;
            else if (option === 'ads_none') isSelected = choices?.ads?.none || false;
          } else if (stepId === 'web') {
            isSelected = choices?.web === option;
          } else if (stepId === 'emails') {
            isSelected = choices?.emails === option;
          }
          
          // Determine if connected
          let isConnected = false;
          if (option === 'meta_ads') isConnected = connections?.meta_connected || false;
          else if (option === 'google_ads') isConnected = connections?.google_connected || false;
          else if (option === 'ghl') isConnected = connections?.ghl_connected || false;
          
          return (
            <div key={option} className="space-y-2">
              <IntegrationCard
                option={option}
                info={info}
                selected={isSelected}
                connected={isConnected}
                onSelect={() => handleSelect(stepId, option)}
                loading={connectingPlatform === option}
              />
              
              {/* Connect button for OAuth platforms */}
              {isSelected && !isConnected && ['meta_ads', 'google_ads', 'ghl'].includes(option) && (
                <div className="space-y-2">
                  <Button
                    onClick={() => handleConnect(option)}
                    disabled={connectingPlatform === option}
                    className="w-full"
                    size="sm"
                  >
                    {connectingPlatform === option ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Link2 className="w-4 h-4 mr-2" />
                    )}
                    Conectar {info?.title}
                  </Button>
                  
                  {/* Manual token option for Meta only */}
                  {option === 'meta_ads' && (
                    <button
                      onClick={() => setShowManualMetaToken(true)}
                      className="w-full text-xs text-text-muted hover:text-brand transition-colors py-1"
                    >
                      ¿Problemas con OAuth? Usa token manual →
                    </button>
                  )}
                </div>
              )}
              
              {/* Disconnect button for connected OAuth platforms */}
              {isSelected && isConnected && ['meta_ads', 'google_ads', 'ghl'].includes(option) && (
                <div className="flex items-center justify-between p-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <span className="text-sm text-green-400">✓ Conectado</span>
                  <button
                    onClick={() => handleDisconnect(option === 'meta_ads' ? 'meta' : option)}
                    disabled={disconnecting === option || disconnecting === 'meta'}
                    className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                  >
                    {(disconnecting === option || disconnecting === 'meta') ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <XCircle className="w-3 h-3" />
                    )}
                    Desconectar
                  </button>
                </div>
              )}
              
              {/* Select Ad Account button for Meta when connected but no account selected */}
              {isSelected && isConnected && option === 'meta_ads' && 
               !funnel?.client_integrations?.meta_ads?.ad_account_id && 
               !funnel?.client_integrations?.meta?.ad_account_id && (
                <Button
                  onClick={() => {
                    setShowMetaAdAccountSelector(true);
                    fetchMetaAdAccounts();
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Seleccionar Cuenta Publicitaria
                </Button>
              )}
              
              {/* Show selected ad account */}
              {isSelected && isConnected && option === 'meta_ads' && 
               (funnel?.client_integrations?.meta_ads?.ad_account_id || funnel?.client_integrations?.meta?.ad_account_id) && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-400">
                      ✓ Cuenta: {funnel.client_integrations.meta_ads?.ad_account_name || 
                                 funnel.client_integrations.meta?.ad_account_name ||
                                 funnel.client_integrations.meta_ads?.ad_account_id || 
                                 funnel.client_integrations.meta?.ad_account_id}
                    </span>
                    <button 
                      onClick={() => {
                        setShowMetaAdAccountSelector(true);
                        fetchMetaAdAccounts();
                      }}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      Cambiar
                    </button>
                  </div>
                </div>
              )}
              
              {/* Select Ad Account button for Google Ads when connected but no account selected */}
              {isSelected && isConnected && option === 'google_ads' && !funnel?.client_integrations?.google_ads?.selected_customer && (
                <Button
                  onClick={() => {
                    setShowGoogleAdsAccountSelector(true);
                    fetchGoogleAdsAccounts();
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Seleccionar Cuenta Publicitaria
                </Button>
              )}
              
              {/* Show selected Google Ads account */}
              {isSelected && isConnected && option === 'google_ads' && funnel?.client_integrations?.google_ads?.selected_customer && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-400">
                      ✓ Cuenta: {funnel.client_integrations.google_ads.selected_customer}
                    </span>
                    <button 
                      onClick={() => {
                        setShowGoogleAdsAccountSelector(true);
                        fetchGoogleAdsAccounts();
                      }}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      Cambiar
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };
  
  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {steps.map((step, idx) => (
          <div key={step.id} className="flex items-center">
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
              ${idx < currentStepIndex 
                ? 'bg-green-500 text-white' 
                : idx === currentStepIndex 
                  ? 'bg-brand text-white' 
                  : 'bg-surface border border-border-subtle text-text-muted'
              }
            `}>
              {idx < currentStepIndex ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
            </div>
            {idx < steps.length - 1 && (
              <div className={`w-12 h-0.5 mx-1 ${idx < currentStepIndex ? 'bg-green-500' : 'bg-border-subtle'}`} />
            )}
          </div>
        ))}
      </div>
      
      {/* Current step header */}
      {currentStep && (
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-text-main">{currentStep.title}</h2>
          <p className="text-text-muted mt-1">{currentStep.description}</p>
          {currentStep.multiple && (
            <p className="text-xs text-brand mt-2">Puedes seleccionar múltiples opciones</p>
          )}
        </div>
      )}
      
      {/* Step content */}
      <div className="mb-8">
        {renderStepContent()}
      </div>
      
      {/* Navigation */}
      <div className="flex items-center justify-between pt-6 border-t border-border-subtle">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStepIndex === 0}
          className="gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Anterior
        </Button>
        
        <Button
          onClick={handleNext}
          disabled={!canContinue()}
          className="gap-2"
        >
          {currentStepIndex === steps.length - 1 ? 'Finalizar' : 'Continuar'}
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Manual Meta Token Modal */}
      {showManualMetaToken && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text-main">Conectar Meta con Token Manual</h3>
              <button 
                onClick={() => setShowManualMetaToken(false)}
                className="text-text-muted hover:text-text-main"
              >
                ✕
              </button>
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm">
              <p className="font-medium text-blue-400 mb-2">📋 Cómo obtener tu token:</p>
              <ol className="list-decimal list-inside space-y-1 text-text-muted">
                <li>Ve a <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Business Manager → System Users</a></li>
                <li>Crea o selecciona un System User</li>
                <li>Haz clic en &quot;Generate New Token&quot;</li>
                <li>Selecciona tu App y los permisos: <code className="bg-surface px-1 rounded">ads_read</code>, <code className="bg-surface px-1 rounded">ads_management</code></li>
                <li>Copia el token generado y pégalo aquí</li>
              </ol>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-text-main">Access Token</label>
              <textarea
                value={manualMetaToken}
                onChange={(e) => setManualMetaToken(e.target.value)}
                placeholder="Pega aquí tu token de acceso de Meta..."
                className="w-full h-24 px-3 py-2 bg-app-bg border border-border-subtle rounded-lg text-sm text-text-main placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand/50 resize-none font-mono"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowManualMetaToken(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveManualMetaToken}
                disabled={savingManualToken || !manualMetaToken.trim()}
                className="flex-1"
              >
                {savingManualToken ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Validando...
                  </>
                ) : (
                  'Conectar'
                )}
              </Button>
            </div>
            
            <p className="text-xs text-text-muted text-center">
              El token se valida automáticamente antes de guardarse
            </p>
          </div>
        </div>
      )}
      
      {/* Meta Ad Account Selector Modal (overlay) */}
      {showMetaAdAccountSelector && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-6">
              <Facebook className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-text-main">Selecciona tu Cuenta Publicitaria</h3>
                <p className="text-sm text-text-muted">Elige la cuenta de Meta Ads donde se crearán las campañas</p>
              </div>
              <button 
                onClick={() => setShowMetaAdAccountSelector(false)}
                className="text-text-muted hover:text-text-main"
              >
                ✕
              </button>
            </div>
            
            {loadingAdAccounts ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-brand" />
                <span className="ml-2 text-text-muted">Cargando cuentas...</span>
              </div>
            ) : metaAdAccounts.length === 0 ? (
              <div className="text-center py-8">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-text-muted">No se encontraron cuentas publicitarias</p>
                <p className="text-sm text-text-muted mt-2">Asegúrate de tener acceso a al menos una cuenta de Meta Ads.</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setShowMetaAdAccountSelector(false)}
                >
                  Cerrar
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {metaAdAccounts.map(account => (
                    <button
                      key={account.id}
                      onClick={() => setSelectedMetaAdAccount(account.id)}
                      className={`
                        w-full p-4 rounded-lg border-2 text-left transition-all
                        ${selectedMetaAdAccount === account.id
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-border-subtle hover:border-blue-500/50 bg-bg-main'
                        }
                      `}
                    >
                      <div className="font-medium text-text-main">{account.name}</div>
                      <div className="text-sm text-text-muted">
                        {account.business_name && `${account.business_name} · `}
                        ID: {account.account_id}
                      </div>
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setShowMetaAdAccountSelector(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveMetaAdAccount}
                    disabled={!selectedMetaAdAccount}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Guardar Cuenta
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Google Ads Account Selector Modal (overlay) */}
      {showGoogleAdsAccountSelector && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-6">
              <Globe className="w-8 h-8 text-green-500" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-text-main">Selecciona tu Cuenta de Google Ads</h3>
                <p className="text-sm text-text-muted">Elige la cuenta donde se crearán las campañas</p>
              </div>
              <button 
                onClick={() => {
                  setShowGoogleAdsAccountSelector(false);
                  setShowGoogleAdsManualInput(false);
                }}
                className="text-text-muted hover:text-text-main"
              >
                ✕
              </button>
            </div>
            
            {/* Manual Input Mode */}
            {showGoogleAdsManualInput ? (
              <div className="space-y-4">
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-300">
                      <p className="font-medium mb-1">Introducir ID manualmente</p>
                      <p className="text-xs text-amber-300/80">
                        Si tu cuenta no aparece en la lista, puedes introducir el ID manualmente.
                        Encuéntralo en <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Ads</a> → esquina superior derecha.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-main">ID de Cuenta de Google Ads *</label>
                  <input
                    type="text"
                    value={manualGoogleAdsId}
                    onChange={(e) => {
                      setManualGoogleAdsId(e.target.value);
                      setGoogleAdsVerificationResult(null); // Reset verification on change
                    }}
                    placeholder="123-456-7890"
                    className="w-full px-4 py-2.5 bg-app-bg border border-border-subtle rounded-lg text-text-main placeholder:text-text-muted focus:border-brand focus:outline-none font-mono"
                    data-testid="manual-google-ads-id-input"
                  />
                  <p className="text-xs text-text-muted">Formato: 123-456-7890 (10 dígitos)</p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-main">
                    ID de Cuenta Administradora (MCC)
                    <span className="ml-1 text-xs text-text-muted font-normal">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    value={manualGoogleAdsManagerId}
                    onChange={(e) => {
                      setManualGoogleAdsManagerId(e.target.value);
                      setGoogleAdsVerificationResult(null); // Reset verification on change
                    }}
                    placeholder="987-654-3210"
                    className="w-full px-4 py-2.5 bg-app-bg border border-border-subtle rounded-lg text-text-main placeholder:text-text-muted focus:border-brand focus:outline-none font-mono"
                    data-testid="manual-google-ads-manager-id-input"
                  />
                  <p className="text-xs text-text-muted">Solo si la cuenta está bajo un MCC (Manager Account)</p>
                </div>
                
                {/* Verification Result */}
                {googleAdsVerificationResult && (
                  <div className={`p-3 rounded-lg ${googleAdsVerificationResult.success 
                    ? 'bg-green-500/10 border border-green-500/30' 
                    : 'bg-red-500/10 border border-red-500/30'}`}
                  >
                    <div className="flex items-center gap-2">
                      {googleAdsVerificationResult.success ? (
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                      <span className={`text-sm ${googleAdsVerificationResult.success ? 'text-green-400' : 'text-red-400'}`}>
                        {googleAdsVerificationResult.message}
                      </span>
                    </div>
                    {googleAdsVerificationResult.success && googleAdsVerificationResult.accountName && (
                      <p className="text-xs text-green-300/70 mt-1 ml-7">
                        Nombre: {googleAdsVerificationResult.accountName}
                      </p>
                    )}
                  </div>
                )}
                
                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowGoogleAdsManualInput(false);
                      setGoogleAdsVerificationResult(null);
                    }}
                    className="flex-1"
                  >
                    ← Volver
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleVerifyGoogleAdsId}
                    disabled={verifyingGoogleAdsId || !manualGoogleAdsId.trim()}
                    className="flex-1"
                    data-testid="verify-google-ads-id-btn"
                  >
                    {verifyingGoogleAdsId ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" />Verificando...</>
                    ) : (
                      <>Verificar</>
                    )}
                  </Button>
                  <Button
                    onClick={handleSaveManualGoogleAdsId}
                    disabled={savingManualGoogleAdsId || !manualGoogleAdsId.trim()}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="save-manual-google-ads-id-btn"
                  >
                    {savingManualGoogleAdsId ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" />Guardando...</>
                    ) : (
                      <><CheckCircle2 className="w-4 h-4 mr-2" />Guardar</>
                    )}
                  </Button>
                </div>
              </div>
            ) : loadingGoogleAdsAccounts ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-brand" />
                <span className="ml-2 text-text-muted">Cargando cuentas...</span>
              </div>
            ) : googleAdsAccounts.length === 0 ? (
              <div className="text-center py-6">
                <XCircle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <p className="text-text-main font-medium">No se encontraron cuentas automáticamente</p>
                <p className="text-sm text-text-muted mt-2 mb-4">
                  Esto puede ocurrir si tienes una estructura compleja de cuentas MCC.
                </p>
                
                {/* Manual entry button when no accounts found */}
                <Button
                  onClick={() => setShowGoogleAdsManualInput(true)}
                  className="bg-green-600 hover:bg-green-700 mb-3"
                  data-testid="enter-google-ads-id-manually-btn"
                >
                  <Plug className="w-4 h-4 mr-2" />
                  Introducir ID manualmente
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowGoogleAdsAccountSelector(false)}
                >
                  Cerrar
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {googleAdsAccounts.map(account => (
                    <button
                      key={account.customer_id}
                      onClick={() => setSelectedGoogleAdsAccount(account.customer_id)}
                      className={`
                        w-full p-4 rounded-lg border-2 text-left transition-all
                        ${selectedGoogleAdsAccount === account.customer_id
                          ? 'border-green-500 bg-green-500/10'
                          : 'border-border-subtle hover:border-green-500/50 bg-bg-main'
                        }
                      `}
                    >
                      <div className="font-medium text-text-main">
                        {account.name || account.descriptive_name || `Cuenta ${account.customer_id}`}
                      </div>
                      <div className="text-sm text-text-muted">ID: {account.customer_id}</div>
                    </button>
                  ))}
                </div>
                
                {/* Manual entry link when accounts are found but user's account is missing */}
                <div className="mt-4 pt-4 border-t border-border-subtle">
                  <button
                    onClick={() => setShowGoogleAdsManualInput(true)}
                    className="w-full text-sm text-text-muted hover:text-brand transition-colors flex items-center justify-center gap-2 py-2"
                    data-testid="enter-google-ads-id-manually-link"
                  >
                    <Plug className="w-4 h-4" />
                    ¿No encuentras tu cuenta? Introduce el ID manualmente
                  </button>
                </div>
                
                <div className="flex gap-3 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowGoogleAdsAccountSelector(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveGoogleAdsAccount}
                    disabled={!selectedGoogleAdsAccount}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Guardar Cuenta
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicIntegrationFlow;
